import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Loader2 } from 'lucide-react';
import { GenieAvatar } from '@/components/GenieAvatar';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { useAgentGroupCounts, useAgentGroups, useAgents } from '@/hooks/agent';
import { cn } from '@/lib/utils';
import { GroupFilterTabs } from '@/pages/UserHub/MyGenies/components/GroupFilterTabs';
import { GroupManagementDialog } from '@/pages/UserHub/MyGenies/components/GroupManagementDialog';
import { Agent } from '@/types/agent';
import { GenieCategories } from '@/types/shared/categories';
import { DEFAULT_BRANDING } from '@/types/ui/branding';
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface GenieCardProps {
  genie: Agent;
  docCount: number;
  onSelect: (genie: Agent) => void;
}

const EMPTY_AGENTS: Agent[] = [];

const GenieViewTab = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const {
    data: agentsData,
    isLoading: isLoadingAgents,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useAgents(30);

  const agents = agentsData?.allAgents ?? EMPTY_AGENTS;
  const totalAgents = agentsData?.total || 0;
  const { data: groups = [] } = useAgentGroups();
  const { data: serverGroupCounts } = useAgentGroupCounts();

  // Intersection observer for infinite scroll
  useEffect(() => {
    if (!fetchNextPage || !hasNextPage || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 },
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const handleGenieClick = useCallback(
    (genie: Agent) => {
      navigate(`/genie-hub/knowledge-base/${genie.id}`);
    },
    [navigate],
  );

  const geniesWithCounts = useMemo(() => {
    return agents
      .map((genie) => {
        const docCount = (genie.knowledge_base || []).length;
        return { genie, docCount };
      })
      .sort((a, b) => b.docCount - a.docCount);
  }, [agents]);

  // Cache search term in lowercase
  const searchLower = useMemo(() => searchTerm.toLowerCase(), [searchTerm]);

  // Filter genies by group and search term
  const filteredGenies = useMemo(() => {
    let result = geniesWithCounts;

    // Apply group filter
    if (selectedGroupId === "ungrouped") {
      result = result.filter(({ genie }) => !genie.group_id);
    } else if (selectedGroupId !== null) {
      const groupIdNum = parseInt(selectedGroupId);
      result = result.filter(({ genie }) => genie.group_id === groupIdNum);
    }

    // Apply search filter
    if (searchTerm) {
      result = result.filter(({ genie }) => {
        const nameMatch = genie.name?.toLowerCase().includes(searchLower);
        const descriptionMatch = genie.description?.toLowerCase().includes(searchLower);
        const welcomeMatch = (genie.welcome_message ?? genie.page?.welcome_message)?.toLowerCase().includes(searchLower);
        const categoryMatch = genie.category?.toLowerCase().includes(searchLower);
        return nameMatch || descriptionMatch || welcomeMatch || categoryMatch;
      });
    }

    return result;
  }, [geniesWithCounts, searchLower, searchTerm, selectedGroupId]);

  if (isLoadingAgents) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 text-genie-primary animate-spin" />
          <span className="text-gray-600">Loading genies...</span>
        </div>
      </div>
    );
  }

  if (agents.length === 0) {
    return (
      <div className="bg-white p-12 rounded-xl border border-genie-border text-center">
        <GenieIcon name="files" size={80} color="#9CA3AF" className="mx-auto mb-6" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Genies Available</h3>
        <p className="text-gray-600 max-w-md mx-auto">
          You don't have any genies yet. Create a genie to start building your knowledge base.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Toolbar — group tabs and search share a single row above the grid */}
      <div className="mb-6">
        <div className="flex flex-col gap-3 border-b border-gray-200 md:flex-row md:items-end md:gap-6">
          <GroupFilterTabs
            groups={groups}
            agents={agents}
            selectedGroupId={selectedGroupId}
            onGroupChange={setSelectedGroupId}
            onCreateGroup={() => setShowGroupDialog(true)}
            totalCount={totalAgents}
            serverGroupCounts={serverGroupCounts}
            className="min-w-0 flex-1 border-b-0"
          />

          <div className="order-first w-full shrink-0 pb-1 md:order-last md:w-72 md:pb-2">
            <div className="w-full p-[1.5px] rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]">
              <div className="rounded-[inherit] bg-white">
                <SearchInput
                  value={searchTerm}
                  onChange={setSearchTerm}
                  placeholder="Search genies..."
                  className="w-full"
                  inputClassName="border-transparent focus-visible:ring-0"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results count */}
        {(searchTerm || selectedGroupId) && (
          <p className="mt-3 text-sm text-gray-600">
            Showing {filteredGenies.length} of {totalAgents}{" "}
            {totalAgents === 1 ? "genie" : "genies"}
          </p>
        )}
      </div>

      {/* No results message */}
      {filteredGenies.length === 0 && (searchTerm || selectedGroupId) ? (
        <div className="text-center py-12">
          <p className="text-gray-500 mb-4">
            {searchTerm ? `No genies found matching "${searchTerm}"` : "No genies in this group"}
          </p>
          <button
            onClick={() => {
              setSearchTerm("");
              setSelectedGroupId(null);
            }}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Clear filters
          </button>
        </div>
      ) : (
        /* Genie Grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredGenies.map(({ genie, docCount }) => (
            <GenieCard
              key={genie.id}
              genie={genie}
              docCount={docCount}
              onSelect={handleGenieClick}
            />
          ))}
        </div>
      )}

      {/* Infinite scroll trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef} className="flex justify-center py-8">
          {isFetchingNextPage ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading more genies...</span>
            </div>
          ) : (
            <Button onClick={() => fetchNextPage()} variant="outline" className="px-6 py-2">
              Load More Genies
            </Button>
          )}
        </div>
      )}

      {/* Group Management Dialog */}
      <GroupManagementDialog
        isOpen={showGroupDialog}
        onClose={() => setShowGroupDialog(false)}
        group={null}
      />
    </div>
  );
};

const GenieCard = memo(({ genie, docCount, onSelect }: GenieCardProps) => {
  const brand = genie.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const hasBranding = Boolean(
    genie.branding &&
    (genie.branding.primaryColor !== DEFAULT_BRANDING.primaryColor ||
      genie.branding.secondaryColor ||
      genie.branding.customAvatarUrl ||
      genie.branding.gradientEnabled),
  );

  const category =
    genie.category_data?.label ||
    GenieCategories.find((cat) => cat.id === genie.category) ||
    GenieCategories.find((cat) => cat.id === "other")!;

  return (
    <button
      key={genie.id}
      onClick={() => onSelect(genie)}
      className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden transition-all duration-300 hover:shadow-lg hover:scale-[1.02] hover:border-genie-primary/50 text-left"
      style={
        hasBranding
          ? {
              boxShadow: `0 1px 3px 0 ${brand}10`,
            }
          : undefined
      }
    >
      <div
        className="h-14 relative"
        style={{
          background:
            genie.branding?.gradientEnabled && genie.branding?.secondaryColor
              ? `linear-gradient(${genie.branding.gradientAngle || 135}deg, ${brand}, ${genie.branding.secondaryColor})`
              : `linear-gradient(135deg, ${category.color}15, ${brand}10)`,
        }}
      >
        <div className="absolute top-3 left-3">
          <span
            className="px-2 py-1 rounded-full text-[10px] font-medium text-white shadow-sm"
            style={{ backgroundColor: category.color }}
          >
            {category.label}
          </span>
        </div>
        <div className="absolute top-3 right-3 flex items-center gap-1.5">
          <div
            className={cn("w-2 h-2 rounded-full", genie.is_active ? "bg-green-500" : "bg-gray-400")}
          />
        </div>
      </div>

      <div className="px-4 pb-4">
        <div className="flex justify-center -mt-4 -mb-2">
          <div className="transform scale-[0.65]">
            <GenieAvatar
              color={brand}
              secondaryColor={genie.branding?.secondaryColor}
              gradientEnabled={genie.branding?.gradientEnabled}
              gradientAngle={genie.branding?.gradientAngle}
              avatarBackgroundOpacity={genie.branding?.avatarBackgroundOpacity}
              speaking={false}
              listening={false}
              isConnecting={false}
              customAvatarUrl={genie.branding?.logoUrl || genie.branding?.customAvatarUrl}
              hasCustomBranding={hasBranding}
            />
          </div>
        </div>

        <h3
          className="text-lg font-bold text-gray-900 mb-2 text-center line-clamp-1"
          style={{ color: brand }}
        >
          {genie.name}
        </h3>

        <p className="text-sm text-gray-600 text-center line-clamp-2 mb-3 min-h-[2.5rem]">
          {(genie.welcome_message ?? genie.page?.welcome_message) || genie.description || "No description"}
        </p>

        <div
          className="flex items-center justify-center gap-2 py-2 px-3 rounded-lg"
          style={{ backgroundColor: `${brand}10` }}
        >
          <FileText className="w-4 h-4" style={{ color: brand }} />
          <span className="text-sm font-medium" style={{ color: brand }}>
            {docCount} {docCount === 1 ? "document" : "documents"}
          </span>
        </div>
      </div>
    </button>
  );
});

export default GenieViewTab;
